import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import { showNotification, showConfirmation } from '../../../Notification';
import ResponsiveDataView from '../../../common/ResponsiveDataView';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import {
  CloudUpload,
  Add,
  Search,
  Edit,
  Delete,
  Business,
  Clear,
  Visibility,
  CheckCircle,
  Cancel,
  DesktopWindows,
  WaterDrop,
  Apartment,
  HealthAndSafety,
  CardMembership,
  MenuBook,
  Settings,
  BusinessCenter
} from '@mui/icons-material';

const DepartmentManagement = ({ user }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSystemsModal, setShowSystemsModal] = useState(false);
  const [modalType, setModalType] = useState(''); // add, edit, view
  const [selectedDept, setSelectedDept] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSystems, setSelectedSystems] = useState([]);
  
  const [logoFile, setLogoFile] = useState(null);

  const [deptForm, setDeptForm] = useState({
    department_code: '',
    department_name: '',
    department_acronym: '',
    description: '',
    department_head: '',
    contact_number: '',
    email: '',
    office_location: '',
    is_active: true,
    logo_url: ''
  });

  const availableSystems = [
    { id: 'water-billing', name: 'Water Billing System', icon: <WaterDrop /> },
    { id: 'property-tax', name: 'Real Property Tax System', icon: <Apartment /> },
    { id: 'business-permit', name: 'Business Permit & Licensing', icon: <BusinessCenter /> },
    { id: 'rural-health', name: 'Rural Health Unit', icon: <HealthAndSafety /> },
    { id: 'civil-registry', name: 'Municipal Civil Registry', icon: <CardMembership /> },
    { id: 'e-logbook', name: 'e-LogBook', icon: <MenuBook /> },
    { id: 'settings', name: 'System Settings', icon: <Settings /> }
  ];

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('lgu_departments')
        .select('*')
        .order('department_name', { ascending: true });
      
      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      showNotification('Error loading departments: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, dept = null) => {
    setModalType(type);
    setLogoFile(null); // Reset file

    if ((type === 'edit' || type === 'view') && dept) {
      setSelectedDept(dept);
      setDeptForm({
        department_code: dept.department_code || '',
        department_name: dept.department_name || '',
        department_acronym: dept.department_acronym || '',
        description: dept.description || '',
        department_head: dept.department_head || '',
        contact_number: dept.contact_number || '',
        email: dept.email || '',
        office_location: dept.office_location || '',
        is_active: dept.is_active !== false,
        logo_url: dept.logo_url || ''
      });
    } else {
      setDeptForm({
        department_code: '',
        department_name: '',
        department_acronym: '',
        description: '',
        department_head: '',
        contact_number: '',
        email: '',
        office_location: '',
        is_active: true,
        logo_url: ''
      });
    }
    setShowModal(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
    }
  };

  const uploadLogo = async (deptId, file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${deptId}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('department_logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('department_logos')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For add, we need an ID before upload if we want to use it in filename, 
      // but usually we can generate one or just use random string. 
      // Supabase insert returns data including ID.
      // Strategy: Use a temp ID or just random for filename if adding.
      // Or, since we want to attach the URL to the insert, we need the URL first.
      
      let logoUrl = deptForm.logo_url;
      const tempId = selectedDept?.id || crypto.randomUUID(); // Use existing ID or random for filename

      if (logoFile) {
        logoUrl = await uploadLogo(tempId, logoFile);
      }

      if (modalType === 'add') {
        const { error } = await supabase
          .from('lgu_departments')
          .insert([{
            ...deptForm,
            logo_url: logoUrl,
            created_by: user?.id,
            created_by_name: `${user?.firstname} ${user?.lastname}`
          }]);
        
        if (error) throw error;
        showNotification('Department added successfully!', 'success');
      } else if (modalType === 'edit') {
        const { error } = await supabase
          .from('lgu_departments')
          .update({
            ...deptForm,
            logo_url: logoUrl
          })
          .eq('id', selectedDept.id);
        
        if (error) throw error;
        showNotification('Department updated successfully!', 'success');
      }

      setShowModal(false);
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deptId) => {
    const confirmed = await showConfirmation('Are you sure you want to delete this department?');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('lgu_departments')
        .delete()
        .eq('id', deptId);
      
      if (error) throw error;
      showNotification('Department deleted successfully!', 'success');
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      showNotification('Error: ' + error.message, 'error');
    }
  };

  const openSystemsModal = async (dept) => {
    setSelectedDept(dept);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('department_systems')
        .select('system_id')
        .eq('department_id', dept.id);
      
      if (error) throw error;
      const systemIds = data.map(d => d.system_id);
      setSelectedSystems(systemIds);
      setShowSystemsModal(true);
    } catch (error) {
      console.error('Error fetching assigned systems:', error);
      showNotification('Error loading systems: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemToggle = (systemId) => {
    setSelectedSystems(prev => {
      if (prev.includes(systemId)) {
        return prev.filter(id => id !== systemId);
      } else {
        return [...prev, systemId];
      }
    });
  };

  const handleSaveSystems = async () => {
    setLoading(true);
    try {
      await supabase
        .from('department_systems')
        .delete()
        .eq('department_id', selectedDept.id);

      if (selectedSystems.length > 0) {
        const assignments = selectedSystems.map(systemId => {
          const system = availableSystems.find(s => s.id === systemId);
          return {
            department_id: selectedDept.id,
            system_id: systemId,
            system_name: system.name,
            created_by: user?.userid,
            created_by_name: `${user?.firstname} ${user?.lastname}`
          };
        });

        const { error } = await supabase
          .from('department_systems')
          .insert(assignments);
        
        if (error) throw error;
      }

      showNotification('Systems assigned successfully!', 'success');
      setShowSystemsModal(false);
    } catch (error) {
      console.error('Error saving systems:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.department_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.department_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.department_acronym?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.department_head?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business /> Department Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => openModal('add')}
        >
          Add Department
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search departments by code, name, acronym, or head..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Paper>

      {/* Responsive Department Management Data View */}
      <ResponsiveDataView
        columns={[
          {
            key: 'code',
            label: 'Code',
            priority: 'high',
            render: (dept) => <strong>{dept.department_code}</strong>
          },
          {
            key: 'name',
            label: 'Department Name',
            priority: 'high',
            render: (dept) => (
              <Typography variant="subtitle2" fontWeight="bold">
                {dept.department_name}
              </Typography>
            )
          },
          {
            key: 'acronym',
            label: 'Acronym',
            priority: 'medium',
            render: (dept) => <Chip label={dept.department_acronym} size="small" variant="outlined" />
          },
          {
            key: 'head',
            label: 'Department Head',
            priority: 'medium',
            render: (dept) => dept.department_head || '—'
          },
          {
            key: 'contact',
            label: 'Contact',
            priority: 'low',
            render: (dept) => dept.contact_number || dept.email || '—'
          },
          {
            key: 'status',
            label: 'Status',
            priority: 'high',
            render: (dept) => (
              <Chip 
                label={dept.is_active ? 'Active' : 'Inactive'} 
                size="small" 
                color={dept.is_active ? 'success' : 'error'}
                icon={dept.is_active ? <CheckCircle /> : <Cancel />}
              />
            )
          },
          {
            key: 'actions',
            label: 'Actions',
            priority: 'high',
            align: 'right',
            render: (dept) => (
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                <Tooltip title="Assign Systems">
                  <IconButton 
                    size="small" 
                    onClick={() => openSystemsModal(dept)}
                    sx={{ color: 'secondary.main' }}
                  >
                    <DesktopWindows />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View Details">
                  <IconButton color="info" size="small" onClick={() => openModal('view', dept)}>
                    <Visibility />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton color="primary" size="small" onClick={() => openModal('edit', dept)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small" onClick={() => handleDelete(dept.id)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }
        ]}
        data={filteredDepartments}
        loading={loading}
        loadingMessage="Loading municipal departments..."
        emptyMessage={searchQuery ? 'No matching departments found' : 'No departments found'}
        rowKey="id"
      />

      {/* Add/Edit/View Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalType === 'add' ? 'Add New Department' : modalType === 'view' ? 'Department Details' : 'Edit Department'}
        </DialogTitle>
        <DialogContent dividers>
          {modalType === 'view' ? (
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle2">Department Code:</Typography> <Typography>{selectedDept?.department_code}</Typography></Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Department Name:</Typography> <Typography>{selectedDept?.department_name}</Typography></Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Acronym:</Typography> <Typography>{selectedDept?.department_acronym}</Typography></Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Description:</Typography> <Typography>{selectedDept?.description || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Head:</Typography> <Typography>{selectedDept?.department_head || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Contact:</Typography> <Typography>{selectedDept?.contact_number || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Email:</Typography> <Typography>{selectedDept?.email || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Location:</Typography> <Typography>{selectedDept?.office_location || '-'}</Typography></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Logo:</Typography>
                {selectedDept?.logo_url ? (
                  <Box sx={{ mt: 1 }}>
                    <img src={selectedDept.logo_url} alt="Department Logo" style={{ maxHeight: 100, maxWidth: 200, objectFit: 'contain' }} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No logo available</Typography>
                )}
              </Grid>
              <Grid item xs={12}><Typography variant="subtitle2">Status:</Typography> <Chip label={selectedDept?.is_active ? 'Active' : 'Inactive'} color={selectedDept?.is_active ? 'success' : 'error'} size="small" /></Grid>
            </Grid>
          ) : (
            <form id="dept-form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Department Code"
                    value={deptForm.department_code}
                    onChange={(e) => setDeptForm({ ...deptForm, department_code: e.target.value.toUpperCase() })}
                    required
                    placeholder="e.g., MAYOR"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Acronym"
                    value={deptForm.department_acronym}
                    onChange={(e) => setDeptForm({ ...deptForm, department_acronym: e.target.value.toUpperCase() })}
                    placeholder="e.g., OMO"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Department Name"
                    value={deptForm.department_name}
                    onChange={(e) => setDeptForm({ ...deptForm, department_name: e.target.value })}
                    required
                    placeholder="e.g., Office of the Mayor"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={deptForm.description}
                    onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Department Head"
                    value={deptForm.department_head}
                    onChange={(e) => setDeptForm({ ...deptForm, department_head: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    value={deptForm.contact_number}
                    onChange={(e) => setDeptForm({ ...deptForm, contact_number: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={deptForm.email}
                    onChange={(e) => setDeptForm({ ...deptForm, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Office Location"
                    value={deptForm.office_location}
                    onChange={(e) => setDeptForm({ ...deptForm, office_location: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={deptForm.is_active}
                        onChange={(e) => setDeptForm({ ...deptForm, is_active: e.target.checked })}
                      />
                    }
                    label="Active Department"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Department Logo (Optional)
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUpload />}
                        size="small"
                      >
                        Upload Logo
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleFileUpload}
                        />
                      </Button>
                      
                      {logoFile ? (
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {logoFile.name}
                        </Typography>
                      ) : deptForm.logo_url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="success.main">
                            Current logo uploaded
                          </Typography>
                          <a href={deptForm.logo_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <Typography variant="caption" color="primary">View</Typography>
                          </a>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          No logo uploaded
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Close</Button>
          {modalType !== 'view' && (
            <Button type="submit" form="dept-form" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : modalType === 'add' ? 'Add Department' : 'Update Department'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Systems Modal */}
      <Dialog open={showSystemsModal} onClose={() => setShowSystemsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Systems to {selectedDept?.department_name}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Select which systems this department can access:
          </Typography>
          <Grid container spacing={2}>
            {availableSystems.map((system) => (
              <Grid item xs={12} sm={6} md={4} key={system.id}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderColor: selectedSystems.includes(system.id) ? 'primary.main' : 'divider',
                    bgcolor: selectedSystems.includes(system.id) ? 'action.selected' : 'background.paper'
                  }}
                >
                  <CardActionArea onClick={() => handleSystemToggle(system.id)}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: selectedSystems.includes(system.id) ? 'primary.main' : 'text.secondary' }}>
                        {system.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {system.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedSystems.includes(system.id) ? 'Access Granted' : 'No Access'}
                        </Typography>
                      </Box>
                      <Checkbox checked={selectedSystems.includes(system.id)} />
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSystemsModal(false)}>Cancel</Button>
          <Button onClick={handleSaveSystems} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save System Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentManagement;
